import { fetchAuthSession } from 'aws-amplify/auth'

/**
 * REST API client for the AWS backend.
 *
 * Every backend call is `POST ${VITE_API_BASE_URL}/${functionName}` with an
 * `Authorization: Bearer <Cognito ID token>` header and a JSON body equal to
 * the old Firebase callable payload. The response is the RAW JSON result (no
 * `{ result }` wrapper). Errors come back as an HTTP status plus a body of
 * `{ error: { code, message } }`.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined

/** Error thrown on a non-2xx response. `.code` mirrors the callable SDK's code. */
export interface ApiError extends Error {
  code?: string
  status?: number
}

/** Fetch the current Cognito ID token, or null if not signed in. */
async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() ?? null
  } catch (err) {
    console.error('Failed to fetch auth session for API call:', err)
    return null
  }
}

/**
 * Low-level call: POSTs the payload and returns the parsed JSON result.
 * Throws an `Error` with `.code` (= `error.code`) and `.message` on non-2xx.
 */
export async function rawCall<T = any>(name: string, payload?: unknown): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured')
  }

  const token = await getIdToken()
  const res = await fetch(`${API_BASE_URL}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload ?? {}),
  })

  // The body may be empty (e.g. 204) — read as text first, then parse.
  const text = await res.text()
  let body: any = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch (err) {
      console.error(`Failed to parse response for ${name}:`, err)
      if (res.ok) throw new Error(`Invalid JSON response from ${name}`)
    }
  }

  if (!res.ok) {
    const errObj = (body && body.error) || {}
    const error = new Error(
      errObj.message || `Request to ${name} failed with status ${res.status}`
    ) as ApiError
    error.code = errObj.code
    error.status = res.status
    throw error
  }

  return body as T
}

/**
 * Firebase `httpsCallable` shim: returns a function `(payload?) => Promise<{ data }>`,
 * mirroring `HttpsCallableResult` so existing call sites (`const res = await fn(p);
 * res.data...`) stay unchanged.
 */
export function apiCallable<TData = any>(name: string) {
  return async (payload?: unknown): Promise<{ data: TData }> => {
    const data = await rawCall<TData>(name, payload)
    return { data }
  }
}
